/**
 * A role code from the shop_role table, such as OWNER or EVENING_COUNTER.
 *
 * <p>It is a plain string rather than a union: the owner creates roles from
 * Settings, so the list is not known when this is compiled.
 */
export type Role = string;

export type Screen =
  | 'BILLING'
  | 'BILLS'
  | 'PURCHASE'
  | 'OUTSTANDING'
  | 'REPORTS'
  | 'MASTERS'
  | 'STOCK'
  | 'SETTINGS'
  | 'USERS'
  | 'SYSTEM';

export type Action = 'view' | 'create' | 'edit' | 'delete';

export interface ScreenPermission {
  canView?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export interface SignedInUser {
  token: string;
  userName: string;
  fullName?: string;
  role: Role;
  roleName?: string;
  permissions: Partial<Record<Screen, ScreenPermission>>;
}

const KEY = 'mobileShopUser';
const TOKEN_KEY = 'mobileShopToken';

/** Who is at the counter right now, as far as this browser knows. */
export const currentUser = (): SignedInUser | null => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SignedInUser) : null;
  } catch {
    return null;
  }
};

export const signIn = (user: SignedInUser) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(user));
    // Kept separately because the API layer reads only the token.
    localStorage.setItem(TOKEN_KEY, user.token);
  } catch {
    /* a locked-down browser just means they log in again next time */
  }
};

export const signOut = () => {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
  // BASE_URL, not '/': live builds are served from a sub-folder and '/' would leave
  // the app entirely.
  window.location.href = import.meta.env.BASE_URL || '/';
};

/**
 * May this user do this, on this screen?
 *
 * <p>The answer comes from the same table the backend enforces, handed over at
 * login. It decides which menu items and which buttons appear - the backend still
 * refuses anything that gets through, so this is about not showing dead buttons,
 * not about security.
 *
 * <p>A permission changed by the owner reaches a browser at its next login.
 */
export const can = (screen: Screen, action: Action = 'view', user = currentUser()): boolean => {
  const permission = user?.permissions?.[screen];
  if (!permission) return false;
  switch (action) {
    case 'create':
      return !!permission.canCreate;
    case 'edit':
      return !!permission.canEdit;
    case 'delete':
      return !!permission.canDelete;
    default:
      return !!permission.canView;
  }
};
